// src/event/resolveEventId.js
// Maps the browser hostname to an eventId (brand). Intentionally duplicated
// in both repos (door + portal) — keep the two copies in sync (behavior, not
// byte-identical: this copy uses single quotes, the door copy double).
//
// Resolution is data-driven and cache-first (MULTI_BRAND_PLATFORM §3a/§4):
//   1. build-time EMBEDDED map (a brand's own domains; the offline-critical door
//      always cold-starts from here, fully synchronous, no network),
//   2. dev/preview ?event= override (persisted), for testing before DNS exists,
//   3. a localStorage host->brand cache (a custom domain resolved on a previous
//      visit boots synchronously like an embedded brand),
//   4. unknown PRODUCTION host -> status 'unknown' (see below).
//
// resolveEventId() always returns a STRING eventId (DEFAULT for unknown hosts)
// so path construction in paths.js can never break and the app never
// white-screens offline. resolveBrand() additionally returns a { status } that
// lets the app distinguish a genuinely-resolved brand from an unknown host.
//
// DEFERRED (public-runtime phase): the pre-app `/brand-runtime.json` fetch that
// resolves a FIRST-visit unknown custom domain by its Host header (§4 path 2)
// is NOT built yet. Until it exists, a brand-new custom domain the bundle has
// never seen resolves to status 'unknown' — the app should show a neutral
// 'brand not configured' shell and block writes rather than silently operating
// as Club Astra against the wrong brand. We do NOT claim unknown custom domains
// fully work until that fetch + its anon brandPublic read rule ship.

const HOSTNAME_EVENT_MAP = {
  'app.clubastra.com.au': 'club-astra',
  'my.clubastra.com.au': 'club-astra',
  'pandathursdays.com': 'panda-thursdays',
  'www.pandathursdays.com': 'panda-thursdays',
  'app.pandathursdays.com': 'panda-thursdays',
  'my.pandathursdays.com': 'panda-thursdays',
  'intimothursdays.com.au': 'intimo-thursdays',
  'www.intimothursdays.com.au': 'intimo-thursdays',
  'app.intimothursdays.com.au': 'intimo-thursdays',
  'my.intimothursdays.com.au': 'intimo-thursdays'
};

export const DEFAULT_EVENT_ID = 'club-astra';
export const KNOWN_EVENT_IDS = ['club-astra', 'panda-thursdays', 'intimo-thursdays'];

// Display metadata for LAUNCHED (active) brands ONLY — the offline-sync source for runtime <head>
// branding (src/event/brandHead.js, §18 #8). A draft/embedded brand still in draft is deliberately
// ABSENT so the head stays neutral until it goes active — matching the brandManifest function +
// firestore.rules.v2 (an unlaunched brand is never exposed). Add a brand here when it goes active.
// Per-brand icons are baked from the uploaded Storage 192 URL (brandPublic/{id}.icon) — re-uploading
// the icon writes a NEW versioned URL, so re-bake this entry if the operator re-uploads.
export const BRAND_DISPLAY = {
  'club-astra': {
    name: 'Club Astra',
    shortName: 'Club Astra',
    themeColor: '#111111',
    icon: '/icons/icon-192.png'
  },
  'panda-thursdays': {
    name: 'Panda Thursdays',
    shortName: 'Panda',
    themeColor: '#971f3a',
    logo: 'https://storage.googleapis.com/club-astra-guestlist-app.firebasestorage.app/brandAssets/panda-thursdays/versions/vmqunhx34-bhjksl3o/logo.png',
    logoSymbol:
      'https://storage.googleapis.com/club-astra-guestlist-app.firebasestorage.app/brandAssets/panda-thursdays/versions/vmquo704g-k8hw58ql/logo-symbol.png',
    icon: 'https://storage.googleapis.com/club-astra-guestlist-app.firebasestorage.app/brandAssets/panda-thursdays/versions/vmqunhx34-bhjksl3o/icon-192.png'
  },
  'intimo-thursdays': {
    name: 'Intimo Thursdays',
    shortName: 'Intimo Thursdays',
    themeColor: '#0d3b2a',
    logo: 'https://storage.googleapis.com/club-astra-guestlist-app.firebasestorage.app/brandAssets/intimo-thursdays/versions/vmv759l14-045us8z0/logo.png',
    icon: 'https://storage.googleapis.com/club-astra-guestlist-app.firebasestorage.app/brandAssets/intimo-thursdays/versions/vmv6qprws-xmb02oi8/icon-192.png'
  }
};

const OVERRIDE_KEY = 'eventIdOverride';
const HOST_CACHE_PREFIX = 'brandHost:'; // brandHost:<hostname> -> eventId

function isDevHost(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com');
}

function readHostCache(host) {
  try {
    const id = localStorage.getItem(HOST_CACHE_PREFIX + host);
    return id || null;
  } catch {
    return null;
  }
}

// Remember an authoritative host->brand resolution so later visits to a custom
// domain boot synchronously (§4 path 3). Exported so the deferred
// /brand-runtime.json fetch can populate it on a first visit.
export function cacheBrandForHost(host, eventId) {
  try {
    if (host && eventId) localStorage.setItem(HOST_CACHE_PREFIX + String(host).toLowerCase(), eventId);
  } catch {
    /* storage unavailable — resolution still applies for this session */
  }
}

// Resolve a hostname to a brand. Returns:
//   { eventId, status: 'resolved' | 'unknown', source }
// eventId is ALWAYS a valid string (DEFAULT for unknown) so callers that need a
// path can never crash; status tells whether the host is a configured brand.
export function resolveBrand(hostname, search) {
  const host = String(hostname || '').toLowerCase();

  // 1. Build-time embedded brand domains (authoritative; offline-safe).
  const mapped = HOSTNAME_EVENT_MAP[host];
  if (mapped) {
    cacheBrandForHost(host, mapped);
    return { eventId: mapped, status: 'resolved', source: 'embedded' };
  }

  // 2. Dev/preview ?event= override (persisted), only on dev hosts.
  if (isDevHost(host)) {
    const params = new URLSearchParams(search != null ? search : window.location.search);
    const q = params.get('event');
    if (q && KNOWN_EVENT_IDS.includes(q)) {
      try {
        localStorage.setItem(OVERRIDE_KEY, q);
      } catch {
        /* persist failed (private mode etc.) — override still applies now */
      }
      return { eventId: q, status: 'resolved', source: 'override' };
    }
    try {
      if (q === '') localStorage.removeItem(OVERRIDE_KEY);
      const stored = localStorage.getItem(OVERRIDE_KEY);
      if (stored && KNOWN_EVENT_IDS.includes(stored)) {
        return { eventId: stored, status: 'resolved', source: 'override' };
      }
    } catch {
      /* storage unavailable — fall through */
    }
    // A dev host with no override is the default brand, not an unknown domain.
    return { eventId: DEFAULT_EVENT_ID, status: 'resolved', source: 'dev-default' };
  }

  // 3. Previously-resolved custom domain (cache-first, synchronous).
  const cached = readHostCache(host);
  if (cached) return { eventId: cached, status: 'resolved', source: 'cache' };

  // 4. Unknown PRODUCTION host. Keep a safe DEFAULT eventId for path
  // construction, but flag it: the app should show a neutral 'brand not
  // configured' shell and block writes, NOT silently serve Club Astra. Fully
  // resolving a first-visit unknown custom domain needs the deferred
  // /brand-runtime.json fetch (see header).
  return { eventId: DEFAULT_EVENT_ID, status: 'unknown', source: 'fallback' };
}

// String-only resolver kept for paths.js (and any caller that just needs the
// eventId for path construction). Same contract as before: always a string,
// DEFAULT for unknown hosts, so the offline-critical door never white-screens.
export function resolveEventId(hostname, search) {
  return resolveBrand(hostname, search).eventId;
}
